import { posterExtract } from '@spo/logic';
import { Modal } from '@spo/storybook';
import { VpButton, VpIcon } from '@vtmn-play/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { InputActionMeta } from 'react-select';
import { useNatureOfferPosterModal } from '../../hooks/useNatureOfferPosterModal';
import { useOfferPosterExtract } from '../../hooks/useOfferPosterExtract';
import { useSportOfferPosterModal } from '../../hooks/useSportOfferPosterModal';
import { useUserConfig } from '../../hooks/useUserConfig';
import { metaActions } from '../../utils/metaActions';
import './offerPosterModal.scss';
import { SelectionInput } from './selectionInput/SelectionInput';

export interface OfferPosterModalProps {
  modalStatus: boolean;
  setModalStatus: (status: boolean) => void;
}

export const OfferPosterModal = ({
  modalStatus,
  setModalStatus,
}: OfferPosterModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedSport, setSelectedSport] = useState<number | null>(null);
  const [selectedNature, setSelectedNature] = useState<number | null>(null);
  const [searchNatureInput, setSearchNatureInput] = useState('');
  const [launchExtract, setLaunchExtract] = useState(false);

  const { data: userConfiguration } = useUserConfig();

  const { data: sports } = useSportOfferPosterModal(userConfiguration);

  const { data: natures } = useNatureOfferPosterModal(
    selectedSport,
    searchNatureInput,
    searchNatureInput.length > 0,
  );

  const {
    data: offerPosterExtractResponse,
    isFetching: isFetchingOfferPosterExtact,
    isError,
  } = useOfferPosterExtract(
    selectedSport,
    selectedNature,
    userConfiguration?.offerDate,
    launchExtract,
  );

  useEffect(() => {
    if (sports?.length === 1) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedSport(sports[0].value);
    }
  }, [sports]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedNature(null);
  }, [selectedSport]);

  useEffect(() => {
    if (isError) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLaunchExtract(false);
    }

    if (!!offerPosterExtractResponse && launchExtract && !isError) {
      const sportLabel = sports
        ?.filter((s) => s.value === selectedSport)[0]
        .name?.replace(/ /g, '-');
      const natureLabel = natures
        ?.filter((n) => n.value === selectedNature)[0]
        .name?.replace(/ /g, '-');
      posterExtract.extractPoster(
        offerPosterExtractResponse,
        `${sportLabel}_${natureLabel}_${userConfiguration?.offerDate}.xlsx`,
      );
      setLaunchExtract(false);
    }
  }, [offerPosterExtractResponse, isError]);

  const displayPosterPage = () => {
    if (selectedSport && selectedNature) {
      const params = new URLSearchParams();
      params.set('sportId', selectedSport.toString());
      params.append('natureId', selectedNature.toString());
      userConfiguration?.offerDate &&
        params.append('offerDate', userConfiguration?.offerDate);
      navigate(`/personal-offer/offer-poster?${params}`);
    }
  };

  const isCtaDisabled = () => !selectedSport || !selectedNature;

  const onInputChange = (search: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action === metaActions.inputChange) {
      setSearchNatureInput(search);
    }
  };

  const closePosterModal = () => {
    setSelectedSport(sports?.length === 1 ? sports[0].value : null);
    setSelectedNature(null);
    setSearchNatureInput('');
    setModalStatus(false);
  };

  const footerModal = (
    <>
      <VpButton
        data-testid="offer-poster_modal_extract-poster"
        variant="secondary"
        size="medium"
        shape="squared"
        onClick={() => setLaunchExtract(true)}
        disabled={isCtaDisabled()}
        loading={isFetchingOfferPosterExtact}
      >
        <VpIcon name="download" />
        {t('offer-poster.modal.extract')}
      </VpButton>
      <VpButton
        data-testid="offer-poster_modal_see-poster"
        size="medium"
        shape="squared"
        onClick={displayPosterPage}
        disabled={isCtaDisabled()}
      >
        {t('offer-poster.modal.see')}
        <VpIcon name="eye" />
      </VpButton>
    </>
  );

  return (
    <Modal
      dataTestid="offer-poster_modal"
      footer={footerModal}
      title={t('offer-poster.title')}
      ariaLabel="Offer Poster Modal"
      isOpen={modalStatus}
      onClose={closePosterModal}
    >
      <div className="offer-poster-modal-subtitle">
        {t('offer-poster.modal.subtitle')}
      </div>
      <SelectionInput
        entities={sports}
        selectedEntity={selectedSport}
        entityType="sport"
        handleEntitySelection={setSelectedSport}
      />
      <SelectionInput
        entities={natures}
        selectedEntity={selectedNature}
        onInputChange={onInputChange}
        entityType="nature"
        handleEntitySelection={setSelectedNature}
        isSelectDisabled={!selectedSport}
      />
      {isError && (
        <div
          className="offer-poster-extract-error-message"
          data-testid="offer-poster_extract_error-message"
        >
          <VpIcon name="warning-error" size={24} />
          {t('offer-poster.modal.extract_error')}
        </div>
      )}
    </Modal>
  );
};
